import type { StreamSliceNode } from '@/analysis/searchContract'
import { useMemo } from 'react'
import { useStore, useFilteredStreams, useStreamIdsForAnalysis } from '@/store'
import ErrorBoundary from '@/components/ErrorBoundary'
import DeepDiveHeader from './deepDiveView/DeepDiveHeader'
import { type SeverityMarker } from './deepDiveView/MetricChart'
import LatencyWaterfall from './deepDiveView/LatencyWaterfall'
import SeverityStrips from './deepDiveView/SeverityStrips'
import SparklineGrid from './deepDiveView/SparklineGrid'
import QueueBullets from './deepDiveView/QueueBullets'
import DeepDiveTable from './deepDiveView/DeepDiveTable'
import { useSeriesData } from '@/hooks/useSeriesData'
import { useDeepDiveRows } from '@/hooks/useDeepDiveRows'
import { findMatchingNode, resolveSelection } from './deepDiveView/selection'
import { toApiWindow, toChartPoints, windowLengthMs, windowRange } from './deepDiveView/TimeWindow'
import { type TimeWindow } from './deepDiveView/types'

export default function DeepDiveView() {
  const filtered = useFilteredStreams()
  const streamIdsForAnalysis = useStreamIdsForAnalysis()
  const drillStream = useStore((s) => s.drillStream)
  const dimension = useStore((s) => s.dimension)
  const incidentSync = useStore((s) => s.incidentSync)
  const incidents = useStore((s) => s.incidents)
  const analysisLoading = useStore((s) => s.analysisLoading)
  const dataDebouncing = useStore((s) => s.dataDebouncing)
  const lastDataError = useStore((s) => s.lastDataError)
  const timeMode = useStore((s) => s.timeMode) as 'live' | 'historic'
  const historicTs = useStore((s) => s.historicTs)
  const historicFromTs = useStore((s) => s.historicFromTs)
  const timeWindow = useStore((s) => s.window) as TimeWindow
  const filters = useStore((s) => s.filters)
  const nowTs = useStore((s) => s.nowTs)
  const search = useStore((s) => s.searchResult)

  const selected = useMemo(
    () => resolveSelection(drillStream, dimension, search?.nodes),
    [drillStream, dimension, search?.nodes],
  )

  const sids = useMemo(
    () => (drillStream ? [drillStream] : streamIdsForAnalysis),
    [drillStream, streamIdsForAnalysis],
  )

  const streamNode = useMemo((): StreamSliceNode | null => {
    if (!drillStream) return null
    const hit = filtered.find(
      (n): n is StreamSliceNode =>
        n.kind === 'stream' && (n.streamId === drillStream || n.value === drillStream),
    )
    return hit ?? null
  }, [drillStream, filtered])

  const { series, error: seriesError } = useSeriesData({
    selected,
    timeMode,
    timeWindow,
    historicTs,
    historicFromTs,
    filters,
    searchNodes: search?.nodes,
  })

  const { rows: tableRows, error: rowsError } = useDeepDiveRows({
    selected,
    drillStream,
    timeMode,
    timeWindow,
    historicTs,
    historicFromTs,
    filters,
    searchNodes: search?.nodes,
  })

  const data = useMemo(() => {
    if (!selected) return []
    const rows = series ?? []
    const { fromMs, toMs } = windowRange({
      windowMs:
        timeWindow === 'custom' && historicFromTs != null && historicTs != null
          ? Math.max(0, Math.abs(historicTs - historicFromTs))
          : windowLengthMs(toApiWindow(timeWindow)),
      timeMode,
      historicTs,
      historicFromTs,
      nowTs,
      rows,
    })
    return toChartPoints(rows, fromMs, toMs)
  }, [selected, series, timeWindow, timeMode, historicTs, historicFromTs, nowTs])

  const severityMarkers = useMemo<SeverityMarker[]>(() => {
    const nodes = search?.nodes
    if (!nodes || !Array.isArray(nodes)) return []
    const markers: SeverityMarker[] = []
    const seen = new Set<number>()
    for (const n of nodes) {
      const sev = n.avgSeverity
      if (sev !== 0 && sev !== 1) continue
      const ts = Number(n.windowEndMs)
      if (!Number.isFinite(ts) || seen.has(ts)) continue
      seen.add(ts)
      markers.push({ ts, severity: sev === 0 ? 'red' : 'yellow' })
    }
    return markers
  }, [search?.nodes])

  void incidentSync
  void incidents

  const isLoading = Boolean(dataDebouncing || analysisLoading)
  const isErrored = Boolean(lastDataError) || Boolean(seriesError) || Boolean(rowsError)
  const contextLabel =
    streamNode && streamNode.kind === 'stream'
      ? [streamNode.streamType, streamNode.country, streamNode.region].filter(Boolean).join(' · ') || null
      : null

  // Waterfall / bullets: drilled stream metrics, else selection match from search.
  const focusMetrics = useMemo(() => {
    if (streamNode) return streamNode.metrics ?? null
    if (!selected) return null
    const node = findMatchingNode(
      search?.nodes,
      selected.value,
      selected.sliceType,
      selected.sliceValue,
    )
    return node?.metrics ?? null
  }, [streamNode, selected, search?.nodes])

  return (
    <ErrorBoundary label="Deep dive panel">
      <div className="panel" style={{ flex: 1, overflow: 'auto' }}>
        <DeepDiveHeader
          drillStream={drillStream}
          aggregateCount={sids.length}
          loading={isLoading}
          errored={isErrored}
          contextLabel={contextLabel}
        />

        <div style={{ padding: '12px 14px 8px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* Stage waterfall + queue/buffer bullets */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(280px, 3fr) minmax(240px, 2fr)',
              gap: 10,
            }}
          >
            <LatencyWaterfall
              metrics={focusMetrics}
              title={
                drillStream
                  ? `Pipeline latency · ${drillStream}`
                  : 'Pipeline latency (selection)'
              }
            />
            <QueueBullets metrics={focusMetrics} />
          </div>

          {/* Per-metric severity strips */}
          <SeverityStrips data={data} />

          {/* FLOW sparklines — click to expand full charts */}
          <SparklineGrid data={data} severityMarkers={severityMarkers} />

          {/* Current-state stream details */}
          <div
            style={{
              border: '0.5px solid var(--bd-1)',
              borderRadius: 'var(--radius)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                padding: '10px 12px 8px',
                fontSize: 10,
                fontWeight: 600,
                color: 'var(--t-1)',
                textTransform: 'uppercase',
                letterSpacing: '.05em',
              }}
            >
              Stream details
              {drillStream ? (
                <span
                  style={{
                    marginLeft: 8,
                    fontWeight: 400,
                    textTransform: 'none',
                    letterSpacing: 0,
                    color: 'var(--t-2)',
                  }}
                >
                  · selected {drillStream}
                </span>
              ) : (
                <span
                  style={{
                    marginLeft: 8,
                    fontWeight: 400,
                    textTransform: 'none',
                    letterSpacing: 0,
                    color: 'var(--t-2)',
                  }}
                >
                  · all windows in range
                </span>
              )}
            </div>
            <DeepDiveTable
              rows={tableRows}
              selectedStreamId={drillStream}
            />
          </div>
        </div>
      </div>
    </ErrorBoundary>
  )
}
